import { observer } from 'mobx-react'
import { useCallback, useContext, useEffect, useState } from 'react'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL } from '@constants/apiUrl'
import { Button, Icon, LoadingSpinner, PdfViewer } from '@components/Generic'
import { PageHeader } from '@/components/SignDocumentCommon/PageHeader/PageHeader'
import { LoadingScreen } from '@/components/Generic/PdfViewer/core/LoadingScreen'
import { actionReport } from '@/methods/actionReport'
import { SignicatBranding } from '@components/SignicatBranding/SignicatBranding'
import { devLog } from '@/methods/devLog'
import { cx } from '@/utils/cx'
import '@/components/SignDocumentCore/SignDocumentCore.css'
import '@/components/Generic/PdfViewer/core/Core.css'
import { onKeyDownHandler } from '@/methods/keyboardAccessibility'

export const EnrollmentAcceptCertificatePage = observer(() => {
  const store = useContext(StoreContext)
  const { signDocumentPage: trans, pdfViewer: transViewer } =
    store.TranslationsState.translationsNew
  const { isMobile } = store.AppState

  const [isLoading, setIsLoading] = useState(true)
  const [isDocReady, setIsDocReady] = useState(false)
  const [btnLoading, setBtnLoading] = useState(false)
  const [pdfUrl, setPdfUrl] = useState('')
  const [checkboxChecked, setCheckboxChecked] = useState(false)
  const [documentError, setDocumentError] = useState<string | null>(null)

  const fetchPresignedUrl = async (): Promise<string> => {
    const { data } = await axiosInstance.get<{ signedUrl: string }>(
      `${API_URL}/electronic-id/user-file-presigned`,
      { withCredentials: true }
    )
    return data.signedUrl
  }

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const url = await fetchPresignedUrl()
        setPdfUrl(url)
        setIsDocReady(true)
        actionReport({
          type: 'event.onboarding-web.agreement.AGREEMENT_DISPLAYED',
          payload: { name: 'certificate', documentNumber: 1, totalDocuments: 1 },
        })
      } catch (e) {
        devLog(e)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDocument()
  }, [])

  const handleCheckboxChange = useCallback(() => {
    setCheckboxChecked((prev) => {
      const next = !prev
      actionReport({
        type: next
          ? 'event.onboarding-web.agreement.AGREEMENT_ACKNOWLEDGED'
          : 'event.onboarding-web.agreement.AGREEMENT_UNACKNOWLEDGED',
        payload: { name: 'certificate' },
      })
      return next
    })
  }, [])

  const handleDownload = async () => {
    try {
      const url = await fetchPresignedUrl()
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      devLog(e)
    }
  }

  const handleSign = async () => {
    setBtnLoading(true)
    actionReport({
      type: 'event.onboarding-web.agreement.ALL_AGREEMENTS_CONFIRMED',
      payload: {},
    })
    store.ScenarioState.nextPage()
  }

  const isButtonDisabled = !checkboxChecked || !isDocReady

  return (
    <div className="sign-doc-core__container sign-document-core">
      <PageHeader header={trans.header} fileList={[]} currentDocIndex={0} />

      {isLoading || !isDocReady ? (
        <LoadingScreen>
          <div className="pdf-core__loading-text">{transViewer?.loading}</div>
        </LoadingScreen>
      ) : (
        <PdfViewer
          file={pdfUrl}
          fileUrl={pdfUrl}
          initialScale={isMobile ? 1.2 : 0.8}
          documentError={documentError}
          setDocumentError={setDocumentError}
        />
      )}

      <div className="sign-doc-core__footer">
        <div className="doc-core__checkbox-form">
          <label
            className="label-checkbox"
            onKeyDown={(event) => onKeyDownHandler(event, handleCheckboxChange)}
            tabIndex={0}
          >
            <input
              type="checkbox"
              name="sign"
              value={checkboxChecked.toString()}
              checked={checkboxChecked}
              id="sign"
              onChange={handleCheckboxChange}
              tabIndex={-1}
            />
            {trans.checkboxLabel}
          </label>
        </div>

        <div className="sign-doc-core__footer-bottom">
          {!isMobile && (
            <SignicatBranding width="fit-content" margin="0" marginMobile="0" onSignDocument />
          )}

          <div className="sign-doc-buttons__wrapper">
            {isDocReady && (
              <a
                className="download-doc-button download-doc-button--margin-0"
                tabIndex={0}
                onClick={handleDownload}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleDownload()
                }}
                role="button"
              >
                <span>{trans.downloadButton}</span>
                <Icon color="currentColor" colorHover="currentColor" type="download" size="24px" />
              </a>
            )}

            <Button
              onClick={handleSign}
              className="sign-doc-buttons__proceed"
              id="page-submit"
              disabled={isButtonDisabled}
            >
              {btnLoading && <LoadingSpinner inButton />}
              <span
                className={cx(
                  'generic__button-text',
                  btnLoading && 'generic__button-text--loading'
                )}
              >
                {trans.proceed}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
})
